// src/pages/ExpertCircle.jsx
// Page publique « Le cercle des experts » (/cercle-experts) : cooptation d'experts,
// commissaires-priseurs et maisons de vente. Maquette ARTICHAUT « Notaires et Experts » (octobre 2026).
//
// Modèle économique affiché (décidé le 2026-10-05, aligné sur le code) :
//  • aucune commission sur la mise en contact ni sur les ventes / honoraires en direct ;
//    10 % unique (décidé le 2026-10-06) uniquement sur un service facturé via la plateforme ;
//  • fiche gratuite ; l'expert voit les collectionneurs qui le contactent ;
//  • il ne paie que s'il accepte un collectionneur venu de la plateforme : 49 € HT, une seule fois
//    par collectionneur ; mises en relation incluses et illimitées avec un abonnement Art Advisor ;
//  • jamais de frais sur ses propres clients.
// Si ce modèle change, mettre à jour ce fichier ET la fonction expert-introduction.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { track, getAnonId } from "@/lib/track";
import { useDemoPage } from "@/lib/useDemoPage";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Trois profils dans le même cercle, validés avec les mêmes critères (décision du 6 oct. 2026) :
// vente, estimation / expertise, conseil / gestion de collection (Art Advisor).
const KINDS = {
  sale: {
    label: "Commissaire-priseur, maison de vente",
    h1: "Quand un collectionneur veut vendre, il connaît déjà votre nom.",
    lead: `${BRAND_NAME} présente des experts, commissaires-priseurs et maisons de vente aux collectionneurs qui envisagent une cession. Une mise en relation. Rien d'autre.`,
    step: { t: "Un collectionneur envisage une vente", d: "Il vous contacte depuis son espace, en précisant les œuvres concernées." },
    direct: "Estimation, mandat, vente : tout se passe entre vous et lui.",
    note: "",
  },
  appraisal: {
    label: "Expert, estimateur",
    h1: "Quand un collectionneur veut faire estimer une œuvre, il connaît déjà votre nom.",
    lead: `${BRAND_NAME} présente des experts et estimateurs aux collectionneurs qui cherchent une estimation, un avis de valeur ou une expertise. Une mise en relation, puis vous travaillez comme vous l'entendez.`,
    step: { t: "Un collectionneur cherche une estimation ou une expertise", d: "Il vous contacte depuis son espace, en précisant l'œuvre et ce qu'il attend." },
    direct: "Consultation, estimation, expertise : vous facturez en direct, ou via la plateforme si vous préférez (10 % sur ce service uniquement).",
    note: "Vous pouvez facturer votre consultation ou votre expertise en direct, sans commission, ou la proposer via la plateforme (10 % sur ce service uniquement).",
  },
  advice: {
    label: "Art Advisor : achat, vente, conseil",
    h1: "Quand un collectionneur cherche un conseil pour acheter, vendre ou gérer sa collection, il connaît déjà votre nom.",
    lead: `${BRAND_NAME} présente des Art Advisors aux collectionneurs qui veulent être accompagnés : choisir et acquérir une œuvre, vendre, structurer et suivre leur collection. Votre espace de travail, vos clients, vos propositions d'acquisition et vos rapports sont déjà là.`,
    step: { t: "Un collectionneur cherche un accompagnement", d: "Il vous contacte depuis son espace (achat, vente ou gestion de sa collection) en décrivant son besoin." },
    direct: "Conseil à l'achat, à la vente, gestion de collection, missions, rapports : vous restez maître de la relation et de vos honoraires, sans commission sur vos missions.",
    note: "Pour un Art Advisor, l'abonnement (à partir de 99 € par mois) inclut les mises en relation sans limite, l'espace clients et les rapports ; aucune commission sur vos missions.",
  },
};

// Message d'entrée tant qu'aucun profil n'est choisi : il couvre les trois profils (vente, estimation, conseil
// à l'achat et à la vente), pour que la page ne paraisse pas réservée aux seules ventes.
const ANY = {
  h1: "Quand un collectionneur cherche un expert, il connaît déjà votre nom.",
  lead: `${BRAND_NAME} présente des experts, commissaires-priseurs, estimateurs et Art Advisors aux collectionneurs qui veulent vendre, faire estimer, acquérir une œuvre ou être conseillés. Une mise en relation. Rien d'autre.`,
  step: { t: "Un collectionneur a un projet", d: "Vente, estimation, achat ou conseil : il vous contacte depuis son espace, en précisant son besoin." },
  direct: "Estimation, mandat, vente, conseil à l'achat : tout se passe entre vous et lui.",
  note: "",
};

const COMMITMENTS = [
  { t: "Aucune commission sur vos ventes", d: `${BRAND_NAME} ne prélève rien sur la mise en contact, ni sur vos ventes ou honoraires en direct. Seul un service que vous choisissez de facturer via la plateforme donne lieu à 10 %.` },
  { t: "Une fiche gratuite", d: "Créer votre fiche et recevoir des demandes ne coûte rien. Vous ne réglez que si vous choisissez d'accepter un collectionneur." },
  { t: "Aucune exclusivité", d: "Vous restez libre de travailler avec qui vous voulez, par ailleurs." },
  { t: "Le client décide", d: "Aucune donnée n'est transmise sans l'accord explicite du collectionneur." },
];

const STEPS = [
  { t: "Vous créez votre fiche", d: "Spécialités, périodes, zone géographique, prestations proposées." },
  { t: "Un collectionneur envisage une vente", d: "Il vous contacte depuis son espace, en précisant les œuvres concernées." },
  { t: "Vous acceptez la mise en relation", d: "Vous voyez son nom et son message. En l'acceptant, vous ouvrez l'échange avec lui." },
  { t: "Vous traitez en direct", d: "Estimation, mandat, vente : tout se passe entre vous et lui." },
];

const COSTS = [
  { k: "Votre fiche", v: "0 €", d: "Visible des collectionneurs. Vous voyez qui vous contacte." },
  { k: "Une mise en relation acceptée", v: "49 € HT", sub: "58,80 € TTC", d: "Réglés par carte, une seule fois par collectionneur. Ensuite, la relation vous appartient." },
  { k: "Avec un abonnement Art Advisor", v: "Incluses", sub: "illimitées", d: "Les mises en relation sont comprises dans l'abonnement (à partir de 99 € / mois)." },
];

const EMPTY = { name: "", house: "", specialty: "", profile_kind: "", experience: "", diplomas: "", referrer: "", contact_email: "", email: "", website: "" };

export default function ExpertCircle() {
  useDemoPage("cercle-experts");
  const [mode, setMode] = useState("apply"); // apply | recommend
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);
  // Lien de recommandation d'un membre : /cercle-experts?ref=<id du membre>. Aucune contrepartie.
  const ref = (() => {
    try { return new URLSearchParams(window.location.search).get("ref") || ""; } catch { return ""; }
  })();

  useEffect(() => {
    track("cercle_page_view", { referrerAid: ref || undefined });
    document.title = `${BRAND_NAME} — Le cercle des experts`;
  }, [ref]);

  const goForm = (m) => {
    if (m) setMode(m);
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const recommend = mode === "recommend";
  const K = KINDS[form.profile_kind] || ANY;
  // Cliquer à nouveau sur le profil sélectionné revient au message général.
  const setKind = (k) => setForm((f) => ({ ...f, profile_kind: f.profile_kind === k ? "" : k }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(recommend ? "Merci d'indiquer le nom du confrère." : "Merci d'indiquer votre nom."); return; }
    if (!form.profile_kind) { setError(recommend ? "Merci de choisir le profil du confrère." : "Merci de choisir votre profil."); return; }
    if (!EMAIL_RE.test(form.email.trim())) { setError("Merci d'indiquer un email valide."); return; }
    if (recommend && !form.referrer.trim()) { setError("Merci d'indiquer votre nom, pour que nous sachions qui recommande."); return; }
    if (form.contact_email.trim() && !EMAIL_RE.test(form.contact_email.trim())) { setError("L'email du confrère n'est pas valide."); return; }
    setSending(true); setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke("expert-circle-request", {
        body: {
          mode,
          name: form.name.trim(),
          house: form.house.trim(),
          specialty: form.specialty.trim(),
          profile_kind: form.profile_kind,
          experience: form.experience.trim(),
          diplomas: form.diplomas.trim(),
          referrer: form.referrer.trim(),
          ref,
          contact_email: form.contact_email.trim().toLowerCase(),
          email: form.email.trim().toLowerCase(),
          website: form.website, // champ piège anti-robots (doit rester vide)
          brand: BRAND_NAME,
          anon_id: getAnonId(),
        },
      });
      if (err || !(data && data.ok)) throw err || new Error("request_failed");
      track("cercle_apply_submit", { meta: { mode, referred: !!ref } });
      setDone(true);
    } catch (err) {
      console.error("[cercle-experts] request:", err);
      setError("Un problème est survenu. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  const input = "px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4] w-full";

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <div className="flex items-center gap-4">
            <a href="#cout" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Ce que cela coûte</a>
            <a href="#cercle" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Le cercle</a>
            <Link to="/login" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Se connecter</Link>
            <button onClick={() => goForm("apply")} className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black">
              Rejoindre le cercle
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-12 md:pt-16 pb-14">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-5">Le cercle des experts · sur invitation</div>
            <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] max-w-3xl">
              {K.h1}
            </h1>
            <p className="mt-6 text-lg text-[#4a453d] leading-relaxed max-w-2xl">
              {K.lead}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-[#8a8378] mr-1">Vous êtes :</span>
              {Object.entries(KINDS).map(([k, v]) => (
                <button type="button" key={k} onClick={() => setKind(k)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.profile_kind === k ? "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" : "border-[#e5e0d8] text-[#4a453d] hover:bg-white"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => goForm("apply")} className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black">
                Demander à rejoindre le cercle
              </button>
              <Link to="/login" className="px-6 py-3 border border-[#1a1a1a] rounded-md text-base hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
                Je suis déjà membre
              </Link>
            </div>
            <p className="mt-4 text-sm text-[#8a8378]">Fiche gratuite. Aucune commission sur vos ventes. Sur invitation.</p>
          </div>

          {/* Exemple de fiche */}
          <figure className="relative">
            <div className="absolute -inset-3 border border-[#d7ccb5] translate-x-2 translate-y-2" aria-hidden="true" />
            <div className="relative bg-white border border-[#ded8cf] p-7">
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs tracking-[0.18em] uppercase text-[#8a8378]">Fiche d'expert</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#d7ccb5] text-[#8b7355]">Exemple</span>
              </div>
              <div className="text-2xl font-bold">Votre nom</div>
              <div className="text-sm text-[#6b6459] mb-5">Votre maison, étude ou cabinet · Ville</div>
              <dl className="grid gap-4 text-sm">
                {[
                  ["Spécialités", "Périodes, artistes, médiums"],
                  ["Prestations", "Vente, estimation, conseil à l'achat"],
                  ["Recommandé par", "Le nom de votre parrain"],
                ].map(([k, v]) => (
                  <div key={k} className="border-t border-[#efe9dd] pt-3">
                    <dt className="text-[11px] tracking-[0.14em] uppercase text-[#8a8378]">{k}</dt>
                    <dd className="mt-1 text-[#4a453d]">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <figcaption className="relative mt-3 text-xs leading-relaxed text-[#6b6459]">
              Ce que voit le collectionneur : votre fiche, vos spécialités, qui vous recommande.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ENGAGEMENTS */}
      <section className="border-y border-[#e5e0d8] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Nos engagements</div>
          <h2 className="text-3xl font-bold mb-8 max-w-3xl">Vous gardez votre client, vos conditions et vos honoraires.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COMMITMENTS.map((c) => (
              <div key={c.t} className="border border-[#e5e0d8] rounded-xl p-5 bg-[#f9f7f4]">
                <h3 className="font-bold mb-2">{c.t}</h3>
                <p className="text-sm text-[#4a453d] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT CELA SE PASSE */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Comment cela se passe</div>
        <h2 className="text-3xl font-bold mb-10">Une mise en relation, en quatre temps.</h2>
        <ol className="grid md:grid-cols-4 gap-6">
          {[STEPS[0], K.step, { ...STEPS[2] }, { ...STEPS[3], d: K.direct }].map((s, i) => (
            <li key={s.t}>
              <div className="w-9 h-9 rounded-full bg-[#1a1a1a] text-[#f9f7f4] flex items-center justify-center text-sm font-bold mb-4">{i + 1}</div>
              <h3 className="font-bold mb-1">{s.t}</h3>
              <p className="text-sm text-[#4a453d] leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* CE QUE CELA COÛTE */}
      <section id="cout" className="border-y border-[#e5e0d8] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Ce que cela coûte</div>
          <h2 className="text-3xl font-bold mb-2">Gratuit tant que vous n'acceptez personne.</h2>
          <p className="text-[#4a453d] mb-10 max-w-2xl">Un seul frais, clair, payé par carte au moment où vous choisissez d'accepter un collectionneur.</p>
          <div className="grid md:grid-cols-3 gap-5">
            {COSTS.map((c, i) => (
              <div key={c.k} className={`rounded-2xl p-6 border ${i === 1 ? "border-[#1a1a1a] bg-[#f9f7f4]" : "border-[#e5e0d8]"}`}>
                <div className="text-sm text-[#6b6459] mb-3">{c.k}</div>
                <div className="text-3xl font-bold">{c.v}</div>
                {c.sub && <div className="text-xs text-[#8a8378] mt-1">{c.sub}</div>}
                <p className="mt-4 text-sm text-[#4a453d] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          {K.note && <p className="mt-6 text-sm text-[#4a453d] bg-[#f9f7f4] border border-[#e5e0d8] rounded-xl px-4 py-3 max-w-3xl">{K.note}</p>}
          <p className="mt-8 text-sm text-[#6b6459]">
            Aucun frais sur vos propres clients. Aucune commission sur la mise en contact ni sur vos ventes ou honoraires en direct ; 10 % uniquement sur un service que vous facturez via la plateforme.
          </p>
        </div>
      </section>

      {/* COOPTATION + FORMULAIRE */}
      <section id="cercle" ref={formRef} className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Cooptation</div>
        <h2 className="text-3xl font-bold mb-3">On entre dans le cercle par un confrère.</h2>
        <p className="text-[#4a453d] leading-relaxed mb-2">
          Chaque membre peut recommander un expert ou un commissaire-priseur de confiance. Nous gardons un cercle choisi, pour que le collectionneur n'ait en face de lui que des professionnels reconnus.
        </p>
        <p className="text-[#4a453d] leading-relaxed mb-8">
          Vous ne connaissez encore personne parmi nous ? Écrivez-nous : nous étudions chaque demande.
        </p>

        {done ? (
          <div className="bg-[#eef5f1] border border-[#cfe3d8] rounded-xl p-8 text-center">
            <div className="text-2xl mb-2">✓</div>
            <h3 className="text-lg font-bold mb-1">Demande enregistrée</h3>
            <p className="text-sm text-[#4a453d]">Merci. Un email de confirmation vient de vous être envoyé (pensez à vérifier vos spams). Nous étudions chaque demande et revenons vers vous.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#e5e0d8] rounded-2xl p-6 md:p-8 grid gap-4">
            <div>
              <label className="block text-xs text-[#8a8378] mb-2">Votre démarche</label>
              <div className="flex flex-wrap gap-2">
                {[["apply", "Je demande à rejoindre le cercle"], ["recommend", "Je recommande un confrère"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setMode(v)}
                    className={`px-4 py-2 rounded-lg text-sm border transition-colors ${mode === v ? "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" : "border-[#e5e0d8] text-[#4a453d] hover:bg-[#f9f7f4]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <select value={form.profile_kind} onChange={field("profile_kind")} className={input} aria-label="Profil">
              <option value="">{recommend ? "Son profil : à choisir *" : "Mon profil : à choisir *"}</option>
              {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{recommend ? `Son profil : ${v.label}` : `Mon profil : ${v.label}`}</option>)}
            </select>
            <input value={form.name} onChange={field("name")} placeholder={recommend ? "Nom et prénom du confrère *" : "Nom et prénom *"} className={input} />
            <input value={form.house} onChange={field("house")} placeholder={recommend ? "Sa maison, son étude ou son cabinet" : "Maison, étude ou cabinet"} className={input} />
            <input value={form.specialty} onChange={field("specialty")} placeholder={recommend ? "Sa spécialité" : "Spécialité"} className={input} />
            {!recommend && (
              <>
                <textarea value={form.experience} onChange={field("experience")} rows={2} placeholder="Votre expérience (années de pratique, ventes, domaines)" className={input} />
                <input value={form.diplomas} onChange={field("diplomas")} placeholder="Diplômes ou agréments" className={input} />
              </>
            )}
            {recommend && (
              <input type="email" value={form.contact_email} onChange={field("contact_email")} placeholder="Email du confrère (facultatif)" className={input} />
            )}
            <input
              value={form.referrer}
              onChange={field("referrer")}
              placeholder={recommend ? "Votre nom *" : "Qui vous recommande ? (nom d'un membre ou d'un confrère)"}
              className={input}
            />
            {!recommend && (
              <p className="text-xs text-[#8a8378] -mt-2">
                {ref
                  ? "Vous arrivez par la recommandation d'un membre du cercle ; elle sera prise en compte."
                  : "Le cercle fonctionne par recommandation. Sans recommandant, votre demande est étudiée au cas par cas : nous pouvons vous écrire avant de l'accepter."}
              </p>
            )}
            <input type="email" required value={form.email} onChange={field("email")} placeholder="Votre email *" className={input} />

            {/* Champ piège anti-robots : invisible pour les humains */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={field("website")}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />

            {error && <p className="text-sm text-[#dc2626]">{error}</p>}
            <button type="submit" disabled={sending} className="px-6 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#b9975a] disabled:opacity-60">
              {sending ? "Envoi…" : "Envoyer ma demande"}
            </button>
            <p className="text-xs text-[#8a8378]">
              Fiche gratuite, aucune commission sur vos ventes. {recommend ? "Votre email sert à vous répondre ; aucun email automatique n'est envoyé à votre confrère. " : ""}
              Vos données restent privées — voir notre{" "}
              <Link to="/privacy-policy" className="underline">politique de confidentialité</Link>.
            </p>
          </form>
        )}
      </section>

      {/* CTA FINAL */}
      <section className="bg-[#1a1a1a] text-[#f9f7f4]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Soyez le nom qu'on recommande.</h2>
          <p className="text-[#d8d2c6] leading-relaxed max-w-2xl mx-auto mb-8">
            Fiche gratuite. Aucune commission sur vos ventes. Sur invitation.
          </p>
          <button onClick={() => goForm("apply")} className="px-7 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-md hover:bg-[#b9975a]">
            Demander à rejoindre le cercle
          </button>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#8a8378] flex flex-wrap gap-4 justify-between">
        <span>© {new Date().getFullYear()} {BRAND_NAME} — Conçue en France · données hébergées dans l'Union européenne</span>
        <span className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-black">Mentions légales</Link>
          <Link to="/privacy-policy" className="hover:text-black">Confidentialité</Link>
        </span>
      </footer>
    </div>
  );
}
